import axios from "axios";
import {camelizeKeys} from "humps";
import {apiUrl} from "../../services.config";
import {reportApiFailure} from "../monitoring";
import {authHeaderValueSelector} from "../../redux/auth";

const REQUEST_TIMEOUT_MS = 15000;
const RETRY_BACKOFFS_MS = [300, 800]; // length === max retries (2); 3 attempts total

const apiClientPublic = axios.create({
    baseURL: apiUrl,
    timeout: REQUEST_TIMEOUT_MS,
    withCredentials: true,
    headers: {
        "X-Requested-With": "XMLHttpRequest",
        "ngrok-skip-browser-warning": "true",
    },
});

const isRetryable = (error: any): boolean => {
    const method = (error?.config?.method ?? "").toLowerCase();
    const noResponse = !error?.response;
    const code = error?.code;
    return (
        method === "get" &&
        noResponse &&
        (code === "ERR_NETWORK" || code === "ECONNABORTED")
    );
};

const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

// Request interceptor: sets X-Site-Domain on every request and forwards the
// Bearer token from Redux state when the user is logged in.
// The store is imported lazily (inside the callback) to avoid a circular
// dependency at module-evaluation time:
//   services/index → shop-public.service → apiClientPublic → redux/store →
//   (all slice barrels) → some reducer references a thunk from another slice
//   that is still being evaluated → undefined.pending crash.
apiClientPublic.interceptors.request.use((config) => {
    config.headers["X-Site-Domain"] = window.location.host;

    // Lazy require so this module does not pull in redux/store at eval time.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { store } = require("../../redux/store") as typeof import("../../redux/store");
    const authHeader = authHeaderValueSelector(store.getState());
    if (authHeader) {
        config.headers["Authorization"] = authHeader;
    }

    return config;
});

// Response interceptor: camelize JSON response bodies (mirrors ApiConfigurator).
apiClientPublic.interceptors.response.use((response) => {
    if (response.data && response.headers["content-type"] === "application/json") {
        response.data = camelizeKeys(response.data);
    }
    return response;
});

// Retry interceptor: same logic as apiClient — retries transient GET failures,
// then reports to Sentry and re-rejects. No 401→logout interceptor on purpose.
apiClientPublic.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error?.config;
        if (config && isRetryable(error)) {
            const attempt: number = config.__retryCount ?? 0;
            if (attempt < RETRY_BACKOFFS_MS.length) {
                config.__retryCount = attempt + 1;
                await delay(RETRY_BACKOFFS_MS[attempt]);
                return apiClientPublic(config);
            }
        }
        if (!error?.response) {
            reportApiFailure(error);
        }
        return Promise.reject(error);
    }
);

export default apiClientPublic;
